import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActionCompletion, Actions, ofActionCompleted, Store } from '@ngxs/store';
import { NzMessageService } from 'ng-zorro-antd/message';
import { of, switchMap, tap } from 'rxjs';
import { getErrorMessage, resolveErrorMessage, updateAndMarkControlAsDirty, UserType } from '@grabbill/lib';
import { NgxsBaseComponent } from '../ngxs-base.component';
import { Authorize, Login, ResolveTenants } from '../../../states/auth/auth.state-actions';
import { SetPageLoading, ShowMessage } from '../../../states/common/common.state-actions';
import { AuthState } from '../../../states/auth/auth.state';
import { verifyUserState } from '../../../utils/verify-user-state';
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { environment } from '../../../environments/environment';
import { isSaasMode } from "../../../utils/deployment-mode";

@Component({
  selector: 'grabbill-client-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent extends NgxsBaseComponent {
  isLoading = false;
  isGoogleLoading = false;
  form: UntypedFormGroup;
  isSaas = isSaasMode();
  currentStep: 'email' | 'credentials' | 'organization' = 'credentials';
  availableTenants: any[] = [];
  selectedTenantId = '';
  ipBlockedError: string | null = null;

  constructor(
    private fb: UntypedFormBuilder,
    protected override store: Store,
    protected override messageService: NzMessageService,
    private actions$: Actions,
    private cd: ChangeDetectorRef
  ) {
    super(store, messageService);

    this.form = this.fb.group({
      email: [null, [Validators.required, Validators.maxLength(255)]],
      password: [null, [Validators.required, Validators.maxLength(255)]],
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.autoUnsubscribe(
      this.actions$.pipe(
        ofActionCompleted(Login, Authorize),
        switchMap((data: ActionCompletion) => {
          this.isLoading = false;
          this.isGoogleLoading = false;
          this.cd.markForCheck();

          if (data.result.error) {
            const errorMsg = getErrorMessage(data.result.error);
            const errCode = (data.result.error as any)?.errorCode;
            if (errCode === 'GRB0018' || errCode === 'GRB0019' || (errorMsg && errorMsg.includes('Your IP address'))) {
              this.ipBlockedError = errorMsg;
            } else {
              this.ipBlockedError = null;
            }
            this.store.dispatch(new ShowMessage('error', errorMsg));
          } else if (data.result.successful) {
            this.ipBlockedError = null;
            const tenants = this.store.selectSnapshot(AuthState.availableTenants) || [];
            const user = this.store.selectSnapshot(AuthState.user);

            if (tenants.length > 0 && (!user || !this.selectedTenantId || this.currentStep === 'credentials')) {
              this.availableTenants = tenants;
              if (!this.selectedTenantId) {
                this.selectedTenantId = tenants[0].tenantId;
              }
              this.currentStep = 'organization';
              this.cd.markForCheck();
              return of(false);
            }

            if (user) {
              this.availableTenants = [];
              if (user.email2FAEnabled || user.google2FAEnabled) {
                return this.navigate(['/', 'two-factor-auth'], {
                  queryParams: {
                    email: this.form.controls['email'].value,
                    tenantId: this.selectedTenantId || undefined
                  }
                });
              }

              const redirectPaths = verifyUserState(user, true);
              if (redirectPaths.paths.length > 0) {
                return this.navigate(redirectPaths.paths, redirectPaths.queryParams);
              }
            }
          }

          return of(false);
        })
      )
    );

    this.autoUnsubscribe(
      this.actions$.pipe(
        ofActionCompleted(ResolveTenants),
        tap((data: ActionCompletion) => {
          this.isLoading = false;
          this.cd.markForCheck();

          if (data.result.successful) {
            const tenants = this.store.selectSnapshot(AuthState.availableTenants) || [];
            this.availableTenants = tenants;
            if (tenants.length > 0) {
              this.selectedTenantId = tenants[0].tenantId;
            }
            if (tenants.length > 1) {
              this.currentStep = 'organization';
            }
            this.cd.markForCheck();
          }
        })
      )
    );
  }

  login(): void {
    if (this.currentStep === 'organization') {
      this.selectOrganizationAndLogin();
      return;
    }

    if (this.form.invalid) {
      updateAndMarkControlAsDirty(this.form);
      return;
    }

    this.isLoading = true;
    this.ipBlockedError = null;
    this.cd.markForCheck();

    const value = this.form.getRawValue();
    this.store.dispatch(new Login({
      email: value.email,
      password: value.password,
      tenantId: this.selectedTenantId || undefined,
      userType: UserType.CLIENT
    }));
  }

  selectOrganizationAndLogin(): void {
    if (!this.selectedTenantId) {
      return;
    }
    this.isLoading = true;
    this.ipBlockedError = null;
    this.cd.markForCheck();

    const value = this.form.getRawValue();
    this.store.dispatch(new Login({
      email: value.email,
      password: value.password,
      tenantId: this.selectedTenantId,
      userType: UserType.CLIENT
    }));
  }

  goBack(): void {
    this.currentStep = 'credentials';
    this.availableTenants = [];
    this.selectedTenantId = '';
    this.store.reset({
      ...this.store.snapshot(),
      auth: {
        ...this.store.selectSnapshot(AuthState),
        availableTenants: []
      }
    });
    this.cd.markForCheck();
  }

  getErrorMessage(control: AbstractControl) {
    return resolveErrorMessage(control);
  }

  doGoogleLogin(): void {
    this.isGoogleLoading = true;
    this.cd.markForCheck();

    const provider = new GoogleAuthProvider();
    const firebaseConfig = environment.config.firebase;
    initializeApp(firebaseConfig);

    const auth = getAuth();

    this.store.dispatch(new SetPageLoading(true));
    signInWithPopup(auth, provider)
      .then(() => {
        auth.currentUser?.getIdToken().then((idToken) => {
          this.store.dispatch(new Authorize({ authProvider: 'google', token: idToken }));
        });
      })
      .catch((error) => {
        this.store.dispatch(new ShowMessage('error', `[${error.code}] ${error.message}`));
        this.isGoogleLoading = false;
        this.cd.markForCheck();
      })
      .finally(() => {
        this.store.dispatch(new SetPageLoading(false));
      });
  }
}
